import { slugFor } from '../../../shared/article-slugs.js';
import { articlePath } from '../../../shared/article-types.js';
import { articlesFor } from '../../../shared/articles.js';
import { getContent } from '../../../shared/content.js';
import { useHref, useLocale } from '../../i18n.js';

/**
 * The article index.
 *
 * No ad unit: like `/guides`, this is signposting rather than an article, and the
 * publisher policy is explicit about ads on screens with little content of their own.
 */

export function Blog() {
  const locale = useLocale();
  const c = getContent(locale).blog;
  const href = useHref();

  // An article with no slug in this language cannot be linked, so it is not listed.
  // The consistency test makes that state a build failure rather than a silent omission.
  const articles = articlesFor(locale).flatMap((article) => {
    const slug = slugFor(article.id, locale);
    return slug === undefined ? [] : [{ article, slug }];
  });

  return (
    <section className="section">
      <div className="section__inner section__inner--narrow">
        <h1 className="section__title">{c.heading}</h1>
        <p className="section__lede">{c.lede}</p>

        {articles.length === 0 ? (
          <p className="callout">{c.empty}</p>
        ) : (
          <ul className="guide-list">
            {articles.map(({ article, slug }) => (
              <li key={article.id}>
                <a className="guide-card" href={href(articlePath(slug))}>
                  <p className="guide-card__who">
                    <time dateTime={article.published}>
                      {c.publishedPrefix} {formatDate(article.published, locale)}
                    </time>
                  </p>
                  <h2 className="guide-card__title">{article.title}</h2>
                  <p className="guide-card__body">{article.summary}</p>
                </a>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

/**
 * `2026-08-22` in the reader's language.
 *
 * Parsed as UTC and formatted as UTC, so a reader west of Greenwich does not see the
 * previous day. The date is editorial, not local to anybody.
 */
export function formatDate(iso: string, locale: string): string {
  const date = new Date(`${iso}T00:00:00Z`);
  return date.toLocaleDateString(locale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}
